import { useEffect, useRef, useState } from 'react';
import ApiError from '@kimuno/api-sdk/lib/utils/ApiError';
import { CheckoutStatusResponse } from '@kimuno/api-sdk/lib/types/responses/Billing';
import services from '../../services';

// The best-effort marketing purchase facts the backend attaches to the
// checkout status (SM-312) — the payload source for the success page's
// `register_premium_finish` push.
export type CheckoutPurchase = NonNullable<CheckoutStatusResponse['purchase']>;

export const POLL_INTERVAL_MS = 2000;
export const POLL_TIMEOUT_MS = 60000;
export const MAX_BACKOFF_MS = 8000;

/**
 * UI states the confirmation page can be in while polling the checkout status.
 * - `confirmingPayment` / `settingUpAccount` are in-progress states.
 * - `ready` is the non-error terminal state: the account is provisioned and the
 *    onboarding token is available, so the user can proceed into profile creation.
 * - `existingAccount` (SM-345) is a non-error terminal state: the buyer is a
 *    returning/reactivated customer who already has a profile (no onboarding
 *    token) — the page shows a "welcome back, please sign in" screen.
 * - `expired`, `sessionNotFound`, `checkoutStatusFailed` are terminal errors.
 * - `emailFallback` is the non-error terminal state (timeout, missing token, or no
 *    session id) — the onboarding email still delivers the link.
 */
export type CheckoutPollState =
  | 'confirmingPayment'
  | 'settingUpAccount'
  | 'ready'
  | 'existingAccount'
  | 'expired'
  | 'sessionNotFound'
  | 'checkoutStatusFailed'
  | 'emailFallback';

export interface CheckoutPollResult {
  state: CheckoutPollState;
  // The email the confirmation/onboarding link was sent to, surfaced so the user
  // can spot a typo. null until a status response includes the provisioned user.
  email: string | null;
  // The onboarding token the profile-creation flow expects. null until the account
  // is provisioned (state === 'ready'); the page uses it to build the onboarding link.
  onboardingToken: string | null;
  // Purchase facts captured from the response that flipped `ready` true. null while
  // polling and when the backend omitted the block (best-effort — the page then
  // skips the tracking push entirely).
  purchase: CheckoutPurchase | null;
  // The provisioned user's id, captured at the `ready` transition — the push's
  // `user_id`. null until ready (or when the response carried no id).
  userId: string | null;
}

/**
 * Polls `billing.getCheckoutStatus` until the user record + onboarding token are
 * ready, then surfaces the `ready` state and the token so the page can offer a
 * "create profile" button into the account-creation flow. Falls back to the
 * "check your email" copy on timeout.
 *
 * Transient errors (502 / network) keep polling within the ceiling; a 404/400 is
 * terminal because the session id can never resolve. Polling stops on every
 * terminal state and on unmount.
 */
const useCheckoutStatusPolling = (sessionId: string | undefined): CheckoutPollResult => {
  const [state, setState] = useState<CheckoutPollState>(
    sessionId ? 'confirmingPayment' : 'emailFallback'
  );
  const [email, setEmail] = useState<string | null>(null);
  const [onboardingToken, setOnboardingToken] = useState<string | null>(null);
  const [purchase, setPurchase] = useState<CheckoutPurchase | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Reset during render (not in the effect) when the session id changes, so no
  // sibling effect can observe the old session's `ready`/purchase/token state
  // paired with the new session id before the new poll starts.
  const [polledSessionId, setPolledSessionId] = useState(sessionId);
  if (sessionId !== polledSessionId) {
    setPolledSessionId(sessionId);
    setState(sessionId ? 'confirmingPayment' : 'emailFallback');
    setEmail(null);
    setOnboardingToken(null);
    setPurchase(null);
    setUserId(null);
  }

  useEffect(() => {
    if (!sessionId) return undefined;

    let isActive = true;
    let elapsedMs = 0;
    let consecutiveErrors = 0;
    let receivedResponse = false;
    let lastErrorState: CheckoutPollState | null = null;

    const stop = () => {
      isActive = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const poll = async () => {
      if (!isActive) return;

      try {
        const status = await services.billing.getCheckoutStatus(sessionId);
        if (!isActive) return;
        receivedResponse = true;
        consecutiveErrors = 0;
        if (status.user) setEmail(status.user.email);

        if (status.paymentStatus === 'expired') {
          setState('expired');
          stop();
          return;
        }

        // SM-345: a returning/reactivated buyer (existing profile, no onboarding
        // token) never flips `ready` — the backend flags them so we surface the
        // "welcome back, please sign in" screen instead of spinning to timeout.
        if (status.existingAccount) {
          if (status.user) setEmail(status.user.email);
          setState('existingAccount');
          stop();
          return;
        }

        // `ready` is the backend's authoritative provisioned-and-onboardable signal
        // (user record + onboarding token both present), independent of `paymentStatus`.
        // Keying off it — instead of `paymentStatus === 'paid'` — is what lets a SEPA
        // buyer proceed within seconds: their debit settles asynchronously, so their
        // `paymentStatus` stays `pending` for days, but they are provisioned optimistically
        // at checkout and `ready` flips true on the next poll. Card buyers are unaffected
        // (`ready` still flips once their paid session provisions).
        if (status.ready && status.onboardingToken) {
          setOnboardingToken(status.onboardingToken);
          // Capture the purchase facts + user id from the response that flipped
          // `ready` — the spec guarantees the (best-effort) block is present no
          // later than this response, so there is no point polling on for it.
          setPurchase(status.purchase ?? null);
          setUserId(status.user?.id ?? null);
          setState('ready');
          stop();
          return;
        }

        // Not ready yet: a captured (paid) payment is provisioning; anything else
        // (pending SEPA / open session) is still confirming.
        setState(status.paymentStatus === 'paid' ? 'settingUpAccount' : 'confirmingPayment');
      } catch (error) {
        if (!isActive) return;
        const statusCode = (error as ApiError)?.statusCode;

        if (statusCode === 404 || statusCode === 400) {
          // Terminal: a missing/malformed session id will never resolve by retrying.
          setState('sessionNotFound');
          stop();
          return;
        }

        // Transient (502 CheckoutStatusFailed / network): retry within the ceiling.
        lastErrorState = 'checkoutStatusFailed';
        consecutiveErrors += 1;
      }

      if (!isActive) return;

      if (elapsedMs >= POLL_TIMEOUT_MS) {
        // Ceiling reached. If we only ever saw transient failures, surface the error;
        // otherwise fall back to the email link — nothing is lost, the email still went out.
        setState(!receivedResponse && lastErrorState ? lastErrorState : 'emailFallback');
        stop();
        return;
      }

      const delay = consecutiveErrors > 0
        ? Math.min(POLL_INTERVAL_MS * 2 ** consecutiveErrors, MAX_BACKOFF_MS)
        : POLL_INTERVAL_MS;
      elapsedMs += delay;
      timeoutRef.current = setTimeout(poll, delay);
    };

    poll();

    return stop;
  }, [sessionId]);

  return { state, email, onboardingToken, purchase, userId };
};

export default useCheckoutStatusPolling;
